/**
 * Superadmin dashboard — the one place platform admins land after logging in.
 *
 * Sections (top to bottom):
 *   1. Stat cards — total admins, pending requests, total current storage,
 *                   total peak storage. Peak is the billing signal (see
 *                   [[project-security-decisions]] — peak-last-30d is the
 *                   metric that survives from Phase 1 into pricing).
 *   2. Pending requests panel — only rendered when the queue is non-empty.
 *                               FIFO order (oldest first). Approve/reject
 *                               inline; approve fires the temp-password email.
 *   3. Admin roster — every ADMIN row with current + peak storage, expiry,
 *                     last login. Per-row actions: Extend +40d, Delete
 *                     (with confirm dialog — cascade is irreversible).
 *
 * All data comes from `use-superadmin.tsx`. The layout gate lives in
 * `SuperadminLayout` + `RequireSuperadmin`. This page assumes it's already
 * inside that shell.
 */

import { useState } from "react";
import {
  AlertTriangle,
  Ban,
  Building2,
  CheckCircle2,
  Check,
  Clock,
  HardDrive,
  Loader2,
  MoreVertical,
  Trash2,
  Users2,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  useSuperadminAdmins,
  useSuperadminApproveRequest,
  useSuperadminDeleteAdmin,
  useSuperadminExpireAdmin,
  useSuperadminRejectRequest,
  useSuperadminRequests,
  useSuperadminRestoreAdmin,
} from "@/hooks/use-superadmin";
import type {
  SuperadminAdmin,
  SuperadminAdminRequest,
} from "@/lib/api-types";
import { cn } from "@/lib/utils";

export default function SuperadminDashboard() {
  const adminsQuery = useSuperadminAdmins();
  const requestsQuery = useSuperadminRequests();

  const admins = adminsQuery.data ?? [];
  const requests = requestsQuery.data ?? [];

  // Aggregate stats — computed client-side over the small list. Rounded to
  // GB display, not TB, until we routinely see >1 TB per workspace.
  const totalCurrent = admins.reduce((sum, a) => sum + (a.currentStorageBytes || 0), 0);
  const totalPeak30d = admins.reduce((sum, a) => sum + (a.peakLast30dBytes || 0), 0);

  return (
    <div className="mx-auto w-full max-w-7xl px-4 py-6 md:px-6 md:py-8">
      {/* Page header */}
      <div className="mb-6 flex items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight md:text-3xl">
            Platform overview
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Every workspace admin, their storage footprint, and pending sign-up requests.
          </p>
        </div>
      </div>

      {/* Stat cards */}
      <div className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-4">
        <StatCard
          icon={<Users2 className="h-4 w-4" />}
          label="Workspaces"
          value={admins.length.toString()}
          loading={adminsQuery.isLoading}
        />
        <StatCard
          icon={<Clock className="h-4 w-4" />}
          label="Pending requests"
          value={requests.length.toString()}
          loading={requestsQuery.isLoading}
          accent={requests.length > 0}
        />
        <StatCard
          icon={<HardDrive className="h-4 w-4" />}
          label="Current storage"
          value={formatBytes(totalCurrent)}
          loading={adminsQuery.isLoading}
        />
        <StatCard
          icon={<HardDrive className="h-4 w-4" />}
          label="Peak — last 30d (billing)"
          value={formatBytes(totalPeak30d)}
          loading={adminsQuery.isLoading}
        />
      </div>

      {/* Pending requests — only shown when queue is non-empty */}
      {requests.length > 0 && (
        <RequestsPanel requests={requests} loading={requestsQuery.isLoading} />
      )}

      {/* Admin roster */}
      <AdminsPanel admins={admins} query={adminsQuery} />
    </div>
  );
}

// ── Stat card ────────────────────────────────────────────────────────────────

function StatCard({
  icon,
  label,
  value,
  loading,
  accent,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  loading?: boolean;
  /** Renders in brand color when non-zero — used for the pending count. */
  accent?: boolean;
}) {
  return (
    <div className="rounded-2xl border border-border bg-card p-4">
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">
        <span className={cn(accent && "text-primary")}>{icon}</span>
        <span>{label}</span>
      </div>
      <div
        className={cn(
          "mt-2 font-display text-2xl font-bold tracking-tight",
          accent && "text-primary",
        )}
      >
        {loading ? <span className="text-muted-foreground">—</span> : value}
      </div>
    </div>
  );
}

// ── Requests panel ───────────────────────────────────────────────────────────

function RequestsPanel({
  requests,
  loading,
}: {
  requests: SuperadminAdminRequest[];
  loading: boolean;
}) {
  return (
    <section className="mb-6">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
          Pending requests ({requests.length})
        </h2>
      </div>
      <div className="overflow-x-auto rounded-2xl border border-border bg-card">
        <table className="w-full min-w-[640px] text-sm">
          <thead className="border-b border-border bg-secondary/40 text-left text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="px-4 py-3 font-medium">Applicant</th>
              <th className="px-4 py-3 font-medium">Company</th>
              <th className="px-4 py-3 font-medium">Submitted</th>
              <th className="px-4 py-3 font-medium text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={4} className="px-4 py-8 text-center text-sm text-muted-foreground">
                  Loading…
                </td>
              </tr>
            ) : (
              requests.map((r) => <RequestRow key={r.id} request={r} />)
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function RequestRow({ request }: { request: SuperadminAdminRequest }) {
  const approve = useSuperadminApproveRequest();
  const reject = useSuperadminRejectRequest();
  const busy = approve.isPending || reject.isPending;

  return (
    <tr className="border-b border-border/60 last:border-b-0 hover:bg-secondary/20">
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          <Avatar name={request.name} />
          <div className="min-w-0">
            <div className="truncate font-medium text-foreground">
              {request.name}
            </div>
            <div className="truncate text-xs text-muted-foreground">
              {request.email}
            </div>
          </div>
        </div>
      </td>
      <td className="px-4 py-3 text-muted-foreground">
        {request.company || <span className="text-muted-foreground/50">—</span>}
      </td>
      <td className="px-4 py-3 text-muted-foreground">
        {formatDate(request.createdAt)}
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center justify-end gap-2">
          <Button
            variant="ghost"
            size="sm"
            className="h-8 gap-1 text-destructive hover:bg-destructive/10 hover:text-destructive"
            disabled={busy}
            onClick={() => reject.mutate({ id: request.id })}
          >
            {reject.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <X className="h-3.5 w-3.5" />}
            Reject
          </Button>
          <Button
            size="sm"
            className="h-8 gap-1"
            disabled={busy}
            onClick={() => approve.mutate({ id: request.id })}
          >
            {approve.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
            Approve
          </Button>
        </div>
      </td>
    </tr>
  );
}

// ── Admins panel ─────────────────────────────────────────────────────────────

function AdminsPanel({
  admins,
  query,
}: {
  admins: SuperadminAdmin[];
  query: ReturnType<typeof useSuperadminAdmins>;
}) {
  return (
    <section>
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
          Workspace admins ({admins.length})
        </h2>
      </div>
      <div className="overflow-x-auto rounded-2xl border border-border bg-card">
        <table className="w-full min-w-[900px] text-sm">
          <thead className="border-b border-border bg-secondary/40 text-left text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="px-4 py-3 font-medium">Admin</th>
              <th className="px-4 py-3 font-medium">Joined</th>
              <th className="px-4 py-3 font-medium">Last login</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 font-medium">Current</th>
              <th className="px-4 py-3 font-medium">Peak (30d)</th>
              <th className="w-10 px-4 py-3" />
            </tr>
          </thead>
          <tbody>
            {query.isLoading ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-sm text-muted-foreground">
                  Loading…
                </td>
              </tr>
            ) : query.isError ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-sm text-destructive">
                  Couldn't load admins.
                </td>
              </tr>
            ) : admins.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-12 text-center">
                  <Building2 className="mx-auto h-6 w-6 text-muted-foreground/60" />
                  <div className="mt-2 text-sm text-muted-foreground">
                    No admins yet.
                  </div>
                </td>
              </tr>
            ) : (
              admins.map((admin) => <AdminRow key={admin.id} admin={admin} />)
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function AdminRow({ admin }: { admin: SuperadminAdmin }) {
  const [expireOpen, setExpireOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const restore = useSuperadminRestoreAdmin();

  const isExpired = isAdminExpired(admin.expiresAt);

  return (
    <tr className="border-b border-border/60 last:border-b-0 hover:bg-secondary/20">
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          <Avatar name={admin.name} />
          <div className="min-w-0">
            <div className="truncate font-medium text-foreground">{admin.name}</div>
            <div className="truncate text-xs text-muted-foreground">{admin.email}</div>
          </div>
        </div>
      </td>
      <td className="px-4 py-3 text-muted-foreground">{formatDate(admin.createdAt)}</td>
      <td className="px-4 py-3 text-muted-foreground">{timeAgo(admin.lastLoginAt)}</td>
      <td className="px-4 py-3">
        <StatusBadge expired={isExpired} value={admin.expiresAt} />
      </td>
      <td className="px-4 py-3 tabular-nums text-foreground">
        {formatBytes(admin.currentStorageBytes)}
      </td>
      <td
        className="px-4 py-3 tabular-nums text-foreground"
        title={
          admin.peakDate
            ? `Peak recorded ${formatDate(admin.peakDate)} (30d window ${admin.windowStart ?? "?"} → ${admin.windowEnd ?? "?"})`
            : "No snapshots yet in the 30d window"
        }
      >
        {formatBytes(admin.peakLast30dBytes)}
      </td>
      <td className="px-4 py-3 text-right">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              aria-label={`Actions for ${admin.name}`}
            >
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>{admin.name}</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {isExpired ? (
              <DropdownMenuItem
                disabled={restore.isPending}
                onSelect={(e) => {
                  e.preventDefault();
                  restore.mutate({ id: admin.id });
                }}
              >
                <CheckCircle2 className="mr-2 h-3.5 w-3.5" />
                Restore access
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem
                className="text-amber-600 focus:text-amber-600 dark:text-amber-400 dark:focus:text-amber-400"
                onSelect={(e) => {
                  e.preventDefault();
                  setExpireOpen(true);
                }}
              >
                <Ban className="mr-2 h-3.5 w-3.5" />
                Expire admin…
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="text-destructive focus:text-destructive"
              onSelect={(e) => {
                e.preventDefault();
                setDeleteOpen(true);
              }}
            >
              <Trash2 className="mr-2 h-3.5 w-3.5" />
              Delete admin…
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <ExpireAdminDialog
          admin={admin}
          open={expireOpen}
          onOpenChange={setExpireOpen}
        />
        <DeleteAdminDialog
          admin={admin}
          open={deleteOpen}
          onOpenChange={setDeleteOpen}
        />
      </td>
    </tr>
  );
}

// ── Expire confirmation ──────────────────────────────────────────────────────

function ExpireAdminDialog({
  admin,
  open,
  onOpenChange,
}: {
  admin: SuperadminAdmin;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const expire = useSuperadminExpireAdmin();

  function onConfirm() {
    expire.mutate(
      { id: admin.id },
      {
        onSuccess: () => onOpenChange(false),
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-600 dark:text-amber-400">
            <Ban className="h-4 w-4" />
            Expire {admin.name}?
          </DialogTitle>
          <DialogDescription className="pt-2">
            The admin and every workspace member under them will be locked out immediately.
            Videos, projects, comments, and share links stay in the database — nothing is
            deleted. Use <strong>Restore access</strong> in the same menu to reverse.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={expire.isPending}>
            Cancel
          </Button>
          <Button
            variant="outline"
            className="border-amber-500/50 text-amber-600 hover:bg-amber-500/10 hover:text-amber-600 dark:text-amber-400 dark:hover:text-amber-400"
            onClick={onConfirm}
            disabled={expire.isPending}
          >
            {expire.isPending ? (
              <>
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                Expiring…
              </>
            ) : (
              "Expire access"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Delete confirmation ──────────────────────────────────────────────────────

function DeleteAdminDialog({
  admin,
  open,
  onOpenChange,
}: {
  admin: SuperadminAdmin;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const del = useSuperadminDeleteAdmin();

  function onConfirm() {
    del.mutate(
      { id: admin.id },
      {
        onSuccess: () => onOpenChange(false),
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="h-4 w-4" />
            Delete {admin.name}?
          </DialogTitle>
          <DialogDescription className="pt-2">
            This permanently deletes the admin account and everything under their workspace —
            all videos (including R2 blobs), projects, editor invites, and every workspace
            member. <strong>This cannot be undone.</strong>
          </DialogDescription>
        </DialogHeader>
        <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-xs text-muted-foreground">
          <div className="flex items-center justify-between">
            <span>Current storage</span>
            <span className="font-medium text-foreground">{formatBytes(admin.currentStorageBytes)}</span>
          </div>
          <div className="mt-1 flex items-center justify-between">
            <span>Peak — last 30d</span>
            <span className="font-medium text-foreground">{formatBytes(admin.peakLast30dBytes)}</span>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={del.isPending}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={onConfirm} disabled={del.isPending}>
            {del.isPending ? (
              <>
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                Deleting…
              </>
            ) : (
              "Delete permanently"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Shared bits ──────────────────────────────────────────────────────────────

function Avatar({ name }: { name: string }) {
  return (
    <div
      aria-hidden
      className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-gradient-to-br from-primary/70 to-primary text-xs font-semibold text-primary-foreground"
    >
      {initialsFrom(name)}
    </div>
  );
}

/**
 * Status badge for the admin row.
 *
 *   expiresAt === null            → Active (green pill)
 *   expiresAt in the past         → Expired (red pill with the date the
 *                                    superadmin flipped the switch)
 *   expiresAt in the future       → shouldn't happen under the new policy
 *                                    (there's no auto-timer anymore), but if
 *                                    the DB carries a legacy future date we
 *                                    fall back to "Active" so the row isn't
 *                                    misleading.
 */
function StatusBadge({ expired, value }: { expired: boolean; value?: string | null }) {
  if (expired) {
    return (
      <span className="inline-flex items-center rounded-full border border-destructive/40 bg-destructive/10 px-2 py-0.5 text-xs font-medium text-destructive">
        Expired {formatDate(value)}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-full border border-emerald-500/40 bg-emerald-500/10 px-2 py-0.5 text-xs font-medium text-emerald-600 dark:text-emerald-400">
      Active
    </span>
  );
}

/**
 * Under the new policy (2026-07-23) admins never auto-expire. A row is
 * expired iff the superadmin manually set `expiresAt` in the past. Any
 * future date (from legacy 40-day-timer rows) is treated as active until
 * the migration 0015 clears them.
 */
function isAdminExpired(value: string | undefined | null): boolean {
  if (!value) return false;
  const t = new Date(value).getTime();
  if (!Number.isFinite(t)) return false;
  return t <= Date.now();
}

// ── Formatting helpers ──────────────────────────────────────────────────────

function initialsFrom(name: string | undefined | null): string {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function formatDate(value: string | undefined | null): string {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function timeAgo(value: string | undefined | null): string {
  if (!value) return "Never";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  const seconds = Math.floor((Date.now() - d.getTime()) / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  const years = Math.floor(days / 365);
  return `${years}y ago`;
}

/**
 * Formats a byte count with the smallest unit that keeps the number under
 * 1024. Rounded to 1 decimal for MB/GB/TB, 0 for KB/B. Handles null/undefined.
 */
function formatBytes(bytes: number | null | undefined): string {
  if (bytes == null || !Number.isFinite(bytes)) return "0 B";
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(0)} KB`;
  const mb = kb / 1024;
  if (mb < 1024) return `${mb.toFixed(1)} MB`;
  const gb = mb / 1024;
  if (gb < 1024) return `${gb.toFixed(1)} GB`;
  const tb = gb / 1024;
  return `${tb.toFixed(2)} TB`;
}
